import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { auth, db } from "../../firebase";
import { onAuthStateChanged, signOut, sendPasswordResetEmail } from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable, getFunctions } from "firebase/functions";
import { Icons } from "../../lib/Icons";

const SoloAccountPage = () => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  
  // Editable fields
  const [displayName, setDisplayName] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/signup/solo");
        return;
      }

      setUser(currentUser);
      await loadUserData(currentUser.uid);
      await loadSubscriptionStatus();
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadUserData = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserProfile(data);
        setDisplayName(data.displayName || "");
        setMarketingConsent(data.marketingConsent || false);
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const functions = getFunctions(undefined, "europe-west3");
      const checkSub = httpsCallable(functions, "checkSoloSubscriptionFunc");
      const result = await checkSub();
      setSubscriptionStatus(result.data);
    } catch (error) {
      console.error("Error loading subscription:", error);
      setSubscriptionStatus({ hasActiveSubscription: false, status: "none" });
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    setMessage({ type: "", text: "" });

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        displayName: displayName.trim(),
        marketingConsent,
        updatedAt: new Date(),
      });
      
      setMessage({ type: "success", text: "Profile updated successfully!" });
      setTimeout(() => setMessage({ type: "", text: "" }), 3000);
    } catch (error) {
      console.error("Error saving profile:", error);
      setMessage({ type: "error", text: "Failed to save profile. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    setMessage({ type: "", text: "" });

    try {
      // Use custom branded email via Cloud Function
      const functions = getFunctions(undefined, "europe-west3");
      const generateResetLink = httpsCallable(functions, "generatePasswordResetLinkFunc");
      await generateResetLink({ email: user.email });
      
      setMessage({ 
        type: "success", 
        text: `Password reset email sent to ${user.email}. Check your inbox!` 
      });
    } catch (error) {
      console.error("Error sending reset email:", error);
      // Fallback to Firebase default
      try {
        await sendPasswordResetEmail(auth, user.email);
        setMessage({ 
          type: "success", 
          text: `Password reset email sent to ${user.email}. Check your inbox!` 
        });
      } catch (fallbackError) {
        setMessage({ type: "error", text: "Failed to send reset email. Please try again." });
      }
    }
  };

  const handleManageSubscription = async () => {
    try {
      const functions = getFunctions(undefined, "europe-west3");
      const createPortal = httpsCallable(functions, "createCustomerPortalFunc");
      const result = await createPortal();
      window.location.href = result.data.url;
    } catch (error) {
      console.error("Error creating portal:", error);
      setMessage({ type: "error", text: "Unable to open subscription management. Please try again." });
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") return;
    setDeleting(true);

    try {
      const functions = getFunctions(undefined, "europe-west3");
      const deleteAccount = httpsCallable(functions, "deleteSoloAccountFunc");
      await deleteAccount();
      
      // Sign out and redirect
      await signOut(auth);
      navigate("/signup/solo");
    } catch (error) {
      console.error("Error deleting account:", error);
      setMessage({ type: "error", text: "Failed to delete account. Please contact support." });
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const getStatusBadge = () => {
    if (!subscriptionStatus) return null;
    
    const { status, trialEndsAt } = subscriptionStatus;
    
    switch (status) {
      case "trialing":
        const trialEnd = trialEndsAt ? new Date(trialEndsAt) : null;
        const daysLeft = trialEnd ? Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24)) : null;
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            Free Trial {daysLeft ? `(${daysLeft} days left)` : ""}
          </span>
        );
      case "active":
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
            Active Subscription
          </span>
        );
      case "past_due":
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-yellow-400 rounded-full"></span>
            Payment Issue
          </span>
        );
      case "cancelled":
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-red-400 rounded-full"></span>
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-2 px-3 py-1 bg-slate-500/20 text-slate-400 rounded-full text-sm font-medium">
            No Subscription
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500/20 border-t-purple-500 mx-auto mb-4"></div>
          <p className="text-slate-400">Loading account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/solo/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img 
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed" 
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-white">SEN-Ed</span>
            </Link>

            <button
              onClick={handleLogout}
              className="text-sm text-slate-400 hover:text-white transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <Link 
            to="/solo/dashboard" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-white">Account Settings</h1>
          <p className="text-slate-400 mt-1">Manage your profile, subscription, and account preferences</p>
        </div>

        {/* Message Banner */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-xl border ${
            message.type === "success" 
              ? "bg-green-500/10 border-green-500/30 text-green-400" 
              : "bg-red-500/10 border-red-500/30 text-red-400"
          }`}>
            {message.text}
          </div>
        )}

        {/* Profile Section */}
        <section className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Profile</h2>
              <p className="text-sm text-slate-400">Your personal information</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
              <input
                type="email"
                value={user?.email || ""}
                disabled
                className="w-full px-4 py-3 bg-slate-700/50 text-slate-400 rounded-xl border border-slate-600/50 cursor-not-allowed"
              />
              <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-4 py-3 bg-slate-700/50 text-white rounded-xl border border-slate-600/50 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
              />
            </div>

            {/* Marketing Consent */}
            <div className="flex items-start gap-3 pt-2">
              <input
                type="checkbox"
                id="marketing"
                checked={marketingConsent}
                onChange={(e) => setMarketingConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500/20"
              />
              <label htmlFor="marketing" className="text-sm text-slate-300">
                I'd like to receive updates about new resources, tips, and special offers
              </label>
            </div>

            <button
              onClick={handleSaveProfile}
              disabled={saving}
              className="mt-4 px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </section>

        {/* Security Section */}
        <section className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Security</h2>
              <p className="text-sm text-slate-400">Manage your password</p>
            </div>
          </div>

          <div className="bg-slate-700/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Password</p>
                <p className="text-sm text-slate-400">Click to receive a password reset email</p>
              </div>
              <button
                onClick={handlePasswordReset}
                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Reset Password
              </button>
            </div>
          </div>
        </section>

        {/* Subscription Section */}
        <section className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Subscription</h2>
              <p className="text-sm text-slate-400">Manage your plan and billing</p>
            </div>
          </div>

          <div className="bg-slate-700/30 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-white font-medium mb-2">Current Plan</p>
                {getStatusBadge()}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">£4.99<span className="text-sm font-normal text-slate-400">/month</span></p>
              </div>
            </div>
          </div>

          {subscriptionStatus?.status === "trialing" && subscriptionStatus.trialEndsAt && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
              <p className="text-green-400 text-sm">
                <strong>Trial ends:</strong> {new Date(subscriptionStatus.trialEndsAt).toLocaleDateString("en-GB", { 
                  weekday: "long", 
                  year: "numeric", 
                  month: "long", 
                  day: "numeric" 
                })}
              </p>
              <p className="text-green-400/70 text-sm mt-1">
                Your card will be charged £4.99/month after the trial ends. Cancel anytime.
              </p>
            </div>
          )}

          {subscriptionStatus?.hasActiveSubscription ? (
            <button
              onClick={handleManageSubscription}
              className="w-full sm:w-auto px-6 py-3 bg-slate-600 hover:bg-slate-500 text-white font-medium rounded-xl transition-colors"
            >
              Manage Subscription
            </button>
          ) : (
            <Link
              to="/solo/checkout"
              className="inline-block w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all text-center"
            >
              Start Free Trial
            </Link>
          )}

          <p className="text-xs text-slate-500 mt-4">
            Subscription management is handled securely by Stripe. You can update payment methods, 
            view invoices, or cancel your subscription in the Stripe portal.
          </p>
        </section>

        {/* Danger Zone */}
        <section className="bg-red-950/30 rounded-2xl border border-red-500/30 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-red-400">Danger Zone</h2>
              <p className="text-sm text-slate-400">Irreversible actions</p>
            </div>
          </div>

          <div className="bg-red-900/20 rounded-xl p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-white font-medium">Delete Account</p>
                <p className="text-sm text-slate-400">Permanently delete your account and all data</p>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 text-sm font-medium rounded-lg transition-colors"
              >
                Delete Account
              </button>
            </div>
          </div>
        </section>

        {/* Support Link */}
        <div className="mt-8 text-center">
          <p className="text-slate-400 text-sm">
            Need help? Contact us at{" "}
            <a href="mailto:support@sen-education.co.uk" className="text-purple-400 hover:text-purple-300 transition-colors">
              support@sen-education.co.uk
            </a>
          </p>
        </div>
      </main>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-2xl border border-slate-700 max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">Delete Account</h3>
                <p className="text-sm text-slate-400">This cannot be undone</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-slate-300 mb-4">
                This will permanently delete your account, including:
              </p>
              <ul className="text-sm text-slate-400 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="text-red-400">•</span> Your profile and preferences
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-400">•</span> Your subscription (will be cancelled)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-red-400">•</span> Access to all resources
                </li>
              </ul>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Type <span className="text-red-400 font-mono">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="w-full px-4 py-3 bg-slate-700/50 text-white rounded-xl border border-slate-600/50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 focus:outline-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText("");
                }}
                className="flex-1 px-4 py-2.5 bg-slate-600 hover:bg-slate-500 text-white font-medium rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== "DELETE" || deleting}
                className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SoloAccountPage;

